const parsePositiveMegabytes = (raw: string | undefined, fallbackMb: number): number => {
  if (raw === undefined || raw === null || String(raw).trim() === "") {
    return fallbackMb
  }

  const parsed = Number.parseInt(String(raw).trim(), 10)
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return fallbackMb
  }

  return parsed
}

const STREAMING_MASTER_MAX_MB = parsePositiveMegabytes(
  import.meta.env.VITE_STREAMING_MEDIA_MAX_FILE_SIZE_MB,
  500
)

export const VENDOR_STREAMING_MASTER_MAX_MEGABYTES = STREAMING_MASTER_MAX_MB

export const VENDOR_STREAMING_MASTER_MAX_BYTES =
  STREAMING_MASTER_MAX_MB * 1024 * 1024

export const VENDOR_STREAMING_MASTER_MIME_TYPES = [
  "audio/flac",
  "audio/wav",
  "audio/mpeg",
  "audio/mp4",
  "audio/x-m4a",
  "audio/aac",
  "audio/ogg",
  "audio/webm",
  "application/ogg",
] as const

export const VENDOR_STREAMING_MASTER_FILE_EXTENSIONS = [
  ".flac",
  ".wav",
  ".mp3",
  ".m4a",
  ".aac",
  ".ogg",
  ".oga",
  ".webm",
] as const

export const isVendorStreamingMasterMimeTypeAllowed = (
  mimeType: string
): boolean => {
  const normalizedMimeType = mimeType.trim().toLowerCase()
  if (!normalizedMimeType) {
    return false
  }

  return (VENDOR_STREAMING_MASTER_MIME_TYPES as readonly string[]).includes(
    normalizedMimeType
  )
}

export const isVendorStreamingMasterFileAllowed = (file: File): boolean => {
  const normalizedName = file.name.trim().toLowerCase()
  const extension = normalizedName.includes(".")
    ? normalizedName.slice(normalizedName.lastIndexOf("."))
    : ""

  if (extension) {
    const isExtensionAllowed = (
      VENDOR_STREAMING_MASTER_FILE_EXTENSIONS as readonly string[]
    ).includes(extension)

    if (isExtensionAllowed) {
      return true
    }
  }

  return isVendorStreamingMasterMimeTypeAllowed(file.type)
}

const MIME_TYPE_BY_EXTENSION: Readonly<Record<string, string>> = {
  ".flac": "audio/flac",
  ".wav": "audio/wav",
  ".mp3": "audio/mpeg",
  ".m4a": "audio/x-m4a",
  ".aac": "audio/aac",
  ".ogg": "audio/ogg",
  ".oga": "audio/ogg",
  ".webm": "audio/webm",
}

/**
 * Declared type for the upload init request. Browsers report empty or
 * non-standard types (e.g. "audio/x-flac") for files the picker accepts by
 * extension; the server only accepts canonical types, so fall back to the
 * extension's canonical type.
 */
export const resolveVendorStreamingMasterMimeType = (file: File): string => {
  if (isVendorStreamingMasterMimeTypeAllowed(file.type)) {
    return file.type.trim().toLowerCase()
  }

  const normalizedName = file.name.trim().toLowerCase()
  const extension = normalizedName.includes(".")
    ? normalizedName.slice(normalizedName.lastIndexOf("."))
    : ""

  return MIME_TYPE_BY_EXTENSION[extension] ?? "application/octet-stream"
}
